import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './StockManagement.css';
import Navigation from '../components/Navigation';
import AddCustomProduct from '../components/POS/AddCustomProduct';
import API_BASE_URL from '../config/api';

const StockManagement = ({ onNavigate, user, token }) => {
  const [stock, setStock] = useState([]);
  const [allStock, setAllStock] = useState([]); // Store all stock for searching
  const [loading, setLoading] = useState(true);
  const [stockSearchLoading, setStockSearchLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [stockSummary, setStockSummary] = useState(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState('low_stock_first');
  const itemsPerPage = 20;
  const [formData, setFormData] = useState({
    medicineRegNumber: '',
    customProductId: '',
    quantity: 0,
    unitPrice: 0, // Selling price
    purchasePrice: 0, // Purchase price
    minStockLevel: 0,
    maxStockLevel: 0,
    expiryDate: '',
    batchNumber: '',
    location: ''
  });
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showAddCustomProduct, setShowAddCustomProduct] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteComment, setDeleteComment] = useState('');

  const authToken = token || localStorage.getItem('pharmacyToken');

  // Define searchProducts before useEffect that uses it
  const searchProducts = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 1) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/pos/products?search=${encodeURIComponent(searchTerm)}&limit=50`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.products || []);
        setShowSearchResults(true); // Always show results (even if empty) so user can add custom product
      } else {
        // Even if response is not ok, show the "no results" message
        setSearchResults([]);
        setShowSearchResults(true);
      }
    } catch (error) {
      // On error, still show "no results" so user can add custom product
      setSearchResults([]);
      setShowSearchResults(true);
    } finally {
      setSearchLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchStockSummary();
  }, []);

  const fetchStockSummary = async () => {
    try {
      setSummaryLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/stock/summary`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStockSummary(data);
      } else {
        setStockSummary(null);
      }
    } catch (error) {
      setStockSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  // Search products when productSearch changes
  useEffect(() => {
    let timeoutId;
    if (productSearch.trim().length >= 1) {
      timeoutId = setTimeout(() => {
        searchProducts(productSearch);
      }, 300);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [productSearch, searchProducts]);

  const fetchStock = async (page = currentPage, sort = sortBy) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sort
      });
      
      const response = await fetch(`${API_BASE_URL}/api/stock?${params}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStock(data.stock || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalItems(data.pagination?.total || 0);
        setCurrentPage(data.pagination?.page || 1);
      }
    } catch (error) {
      // No alerts (per requirement)
    } finally {
      setLoading(false);
    }
  };

  // Fetch all stock for searching (no pagination)
  const fetchAllStock = async (sort = sortBy) => {
    try {
      setStockSearchLoading(true);
      const params = new URLSearchParams({
        page: '1',
        limit: '10000', // High limit to get all items
        sortBy: sort
      });
      
      const response = await fetch(`${API_BASE_URL}/api/stock?${params}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllStock(data.stock || []);
      }
    } catch (error) {
      // No alerts (per requirement)
    } finally {
      setStockSearchLoading(false);
    }
  };

  // Refetch when page or sort changes (only if not searching)
  useEffect(() => {
    if (!searchTerm.trim()) {
      fetchStock(currentPage, sortBy);
    }
  }, [currentPage, sortBy]);

  // Debounced search - fetch all stock when searching
  useEffect(() => {
    let timeoutId;
    
    if (searchTerm.trim()) {
      // Debounce: wait 500ms after user stops typing
      timeoutId = setTimeout(() => {
        fetchAllStock(sortBy);
      }, 500);
    } else {
      // Clear search results and reset to paginated view
      setAllStock([]);
      if (currentPage === 1) {
        fetchStock(1, sortBy);
      } else {
        setCurrentPage(1);
      }
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [searchTerm, sortBy]);

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      stockId: item.id, // Include stock ID for editing
      medicineRegNumber: item.medicine_reg_number || '',
      customProductId: item.custom_product_id || '',
      quantity: item.quantity || 0,
      unitPrice: parseFloat(item.unit_price || 0), // Selling price
      purchasePrice: parseFloat(item.purchase_price || 0), // Purchase price
      minStockLevel: item.min_stock_level || 0,
      maxStockLevel: item.max_stock_level || 0,
      expiryDate: item.expiry_date ? item.expiry_date.split('T')[0] : '',
      batchNumber: item.batch_number || '',
      location: item.location || ''
    });
    setProductSearch(item.medicine_name || item.custom_product_name || '');
    setSearchResults([]);
    setShowSearchResults(false);
    setShowForm(true);
  };

  const handleAddBatch = (item) => {
    // Add new batch for existing product
    setSelectedItem(null);
    setFormData({
      stockId: null,
      medicineRegNumber: item.medicine_reg_number || '',
      customProductId: item.custom_product_id || '',
      quantity: 0,
      unitPrice: parseFloat(item.unit_price || 0), // Use latest batch price as default
      purchasePrice: 0,
      minStockLevel: item.min_stock_level || 0,
      maxStockLevel: item.max_stock_level || 0,
      expiryDate: '',
      batchNumber: '',
      location: item.location || ''
    });
    setProductSearch(item.medicine_name || item.custom_product_name || '');
    setSearchResults([]);
    setShowSearchResults(false);
    setShowForm(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteComment('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteComment.trim()) {
      setMessage('Please provide a reason for deletion.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/stock/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          stockId: itemToDelete.id,
          comment: deleteComment.trim()
        })
      });

      if (response.ok) {
        setMessage('Stock batch deleted successfully.');
        setShowDeleteModal(false);
        setItemToDelete(null);
        setDeleteComment('');
        fetchStock(currentPage, sortBy);
        fetchStockSummary();
      } else {
        const error = await response.json();
        setMessage(error.error || 'Failed to delete stock batch.');
      }
    } catch (error) {
      setMessage('Error deleting stock batch.');
    }
  };

  const handleAddNew = () => {
    setSelectedItem(null);
    setFormData({
      stockId: null,
      medicineRegNumber: '',
      customProductId: '',
      quantity: 0,
      unitPrice: 0, // Selling price
      purchasePrice: 0, // Purchase price
      minStockLevel: 0,
      maxStockLevel: 0,
      expiryDate: '',
      batchNumber: '',
      location: ''
    });
    setProductSearch('');
    setSearchResults([]);
    setShowSearchResults(false);
    setShowForm(true);
  };

  const handleProductSelect = (product) => {
    if (product.isCustom) {
      setFormData(prev => ({
        ...prev,
        customProductId: product.custom_product_id || product.id,
        medicineRegNumber: '',
        unitPrice: product.price_rs || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        medicineRegNumber: product.reg_number,
        customProductId: '',
        unitPrice: product.price_rs || 0
      }));
    }
    setProductSearch(product.product_name);
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const handleCustomProductAdded = async (customProduct) => {
    // After custom product is created, search again to get it with proper format
    // Or format it to match the expected product structure
    const formattedProduct = {
      product_name: customProduct.name,
      custom_product_id: customProduct.id,
      id: customProduct.id,
      isCustom: true,
      price_rs: customProduct.price || 0
    };
    
    // Select the newly created product
    handleProductSelect(formattedProduct);
    
    // Close the add custom product modal
    setShowAddCustomProduct(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!formData.medicineRegNumber && !formData.customProductId) {
      setMessage('Please search and select a product (medicine or custom product), or add a new custom product.');
      return;
    }

    try {
      const payload = {
        ...formData,
        stockId: formData.stockId || undefined // Only include stockId if it exists
      };
      const response = await fetch(`${API_BASE_URL}/api/stock/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage(selectedItem ? 'Stock batch updated successfully.' : 'Stock batch added successfully.');
        setShowForm(false);
        fetchStock(currentPage, sortBy);
        fetchStockSummary();
      } else {
        const error = await response.json();
        setMessage(error.error || 'Failed to update stock.');
      }
    } catch (error) {
      setMessage('Error updating stock.');
    }
  };

  // Filter stock - use allStock when searching, otherwise use paginated stock
  const filteredStock = useMemo(() => {
    const stockToFilter = searchTerm.trim() ? allStock : stock;
    
    if (!searchTerm.trim()) {
      return stockToFilter;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return stockToFilter.filter(item => {
      const productName = (item.medicine_name || item.custom_product_name || '').toLowerCase();
      return productName.includes(searchLower);
    });
  }, [stock, allStock, searchTerm]);

  // Group batches by product (medicine_reg_number or custom_product_id)
  const groupedStock = filteredStock.reduce((groups, item) => {
    const productKey = item.medicine_reg_number || `custom_${item.custom_product_id}`;
    if (!groups[productKey]) {
      groups[productKey] = {
        productName: item.medicine_name || item.custom_product_name,
        medicineRegNumber: item.medicine_reg_number,
        customProductId: item.custom_product_id,
        batches: []
      };
    }
    groups[productKey].batches.push(item);
    return groups;
  }, {});

  // Convert grouped object to array for rendering
  const stockGroups = Object.values(groupedStock);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="stock-management-container">
        <div className="loading">Loading stock...</div>
      </div>
    );
  }

  return (
    <div className="stock-management-container">
      <Navigation currentPage="stock" onNavigate={onNavigate} />
      <div className="stock-header">
        <h1>📦 Stock Management</h1>
        <button className="btn-add" onClick={handleAddNew}>
          ➕ Add Stock
        </button>
      </div>

      <div className="stock-summary">
        <div className="summary-card">
          <div className="summary-label">Total Products (in stock)</div>
          <div className="summary-value">{summaryLoading ? '...' : (stockSummary?.total_products ?? 0)}</div>
          <div className="summary-sub">
            {summaryLoading ? '' : `${stockSummary?.medicines_in_stock ?? 0} medicines • ${stockSummary?.custom_products_in_stock ?? 0} custom`}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Units</div>
          <div className="summary-value">{summaryLoading ? '...' : (stockSummary?.total_units_in_stock ?? 0)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Stock Value (Purchase)</div>
          <div className="summary-value">{summaryLoading ? '...' : formatPrice(stockSummary?.total_purchase_value)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Stock Value (Selling)</div>
          <div className="summary-value">{summaryLoading ? '...' : formatPrice(stockSummary?.total_selling_value)}</div>
        </div>
      </div>

      {!!message && <div className="stock-message">{message}</div>}

      <div className="stock-controls">
        <div className="stock-search">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="stock-sort">
          <label>Sort by:</label>
          <select 
            value={sortBy} 
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
            className="sort-select"
          >
            <option value="low_stock_first">Low Stock First</option>
            <option value="name">Name (A-Z)</option>
            <option value="created_at">Oldest First</option>
          </select>
        </div>
      </div>

      <div className="stock-list">
        {(loading || stockSearchLoading) ? (
          <div className="loading">Loading stock...</div>
        ) : stockGroups.length === 0 ? (
          <div className="empty-state">
            <p>No stock items found</p>
            <button className="btn-add" onClick={handleAddNew}>
              Add First Stock Item
            </button>
          </div>
        ) : (
          <>
            <div className="stock-pagination-info">
              {searchTerm.trim() ? (
                <span>Found {filteredStock.length} product{filteredStock.length !== 1 ? 's' : ''} matching "{searchTerm}"</span>
              ) : (
                <span>Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} products</span>
              )}
            </div>
            {stockGroups.map((group) => (
            <div key={group.medicineRegNumber || `custom_${group.customProductId}`} style={{ marginBottom: '20px' }}>
              <table className="stock-table">
                <thead>
                  <tr>
                    <th colSpan="10" style={{ textAlign: 'left', backgroundColor: '#f0f0f0', padding: '10px', fontSize: '16px', fontWeight: 'bold' }}>
                      {group.productName}
                    </th>
                  </tr>
                  <tr>
                    <th>Batch Number</th>
                    <th>Expiry Date</th>
                    <th>Quantity</th>
                    <th>Purchase Price</th>
                    <th>Sell Price</th>
                    <th>Profit/Unit</th>
                    <th>Total Value</th>
                    <th>Location</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {group.batches.map((item) => {
                    const purchasePrice = parseFloat(item.purchase_price || 0);
                    const sellPrice = parseFloat(item.unit_price || 0);
                    const profitPerUnit = sellPrice - purchasePrice;
                    return (
                      <tr key={item.id} className={item.quantity <= item.min_stock_level ? 'low-stock' : ''}>
                        <td>{item.batch_number || '-'}</td>
                        <td>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '-'}</td>
                        <td>
                          <span className={item.quantity <= item.min_stock_level ? 'quantity-low' : ''}>
                            {item.quantity}
                          </span>
                        </td>
                        <td>{formatPrice(purchasePrice)}</td>
                        <td>{formatPrice(sellPrice)}</td>
                        <td className={profitPerUnit >= 0 ? 'profit-positive' : 'profit-negative'}>
                          {formatPrice(profitPerUnit)}
                        </td>
                        <td>{formatPrice(sellPrice * (item.quantity || 0))}</td>
                        <td>{item.location || '-'}</td>
                        <td>
                          <button className="btn-edit" onClick={() => handleEdit(item)} style={{ marginRight: '5px' }}>
                            ✏️ Edit
                          </button>
                          <button className="btn-add" onClick={() => handleAddBatch(item)} style={{ marginRight: '5px', fontSize: '12px', padding: '5px 10px' }}>
                            ➕ Batch
                          </button>
                          <button className="btn-delete" onClick={() => handleDeleteClick(item)} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            ))}
            {!searchTerm.trim() && (
              <div className="stock-pagination">
                <button 
                  className="pagination-btn" 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  className="pagination-btn" 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedItem ? 'Edit Stock Batch' : 'Add Stock Batch'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="stock-form">
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Search Product (Medicine or Custom Product) *</label>
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    // useEffect will handle the search with debounce
                  }}
                  onFocus={() => {
                    // Show search results when input is focused if there's a search term
                    if (productSearch.trim().length >= 1) {
                      setShowSearchResults(true);
                    }
                  }}
                  onBlur={() => {
                    // Close dropdown after a short delay to allow click events
                    setTimeout(() => {
                      setShowSearchResults(false);
                    }, 200);
                  }}
                  placeholder="Type product name to search..."
                  required
                />
                {searchLoading && (
                  <div style={{ position: 'absolute', right: '10px', top: '45px' }}>Loading...</div>
                )}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="product-search-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    marginTop: '5px'
                  }}>
                    {searchResults.map((product, index) => (
                      <div
                        key={index}
                        className="product-search-item"
                        onClick={() => handleProductSelect(product)}
                        style={{
                          padding: '12px',
                          cursor: 'pointer',
                          borderBottom: index < searchResults.length - 1 ? '1px solid #eee' : 'none'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        <div style={{ fontWeight: '600', color: '#333' }}>
                          {product.product_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {product.isCustom ? (
                            <>Custom Product • Price: PKR {product.price_rs || 0}</>
                          ) : (
                            <>Reg: {product.reg_number} • {product.generic_name} • PKR {product.price_rs || 0}</>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(showSearchResults && searchResults.length === 0 && productSearch.trim().length >= 1 && !searchLoading) && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '12px',
                    zIndex: 1000,
                    marginTop: '5px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>
                      No products found matching "{productSearch}"
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAddCustomProduct(true);
                      }}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        width: '100%'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
                    >
                      ➕ Add "{productSearch}" as Custom Product
                    </button>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Medicine Registration Number (auto-filled if medicine selected)</label>
                  <input
                    type="text"
                    value={formData.medicineRegNumber}
                    onChange={(e) => setFormData({ ...formData, medicineRegNumber: e.target.value })}
                    placeholder="Auto-filled from search"
                    readOnly={!!productSearch}
                  />
                </div>
                <div className="form-group">
                  <label>Custom Product ID (auto-filled if custom product selected)</label>
                  <input
                    type="number"
                    value={formData.customProductId}
                    onChange={(e) => setFormData({ ...formData, customProductId: e.target.value })}
                    placeholder="Auto-filled from search"
                    readOnly={!!productSearch}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Purchase Price (PKR) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.purchasePrice}
                    onChange={(e) => setFormData({ ...formData, purchasePrice: parseFloat(e.target.value) || 0 })}
                    min="0"
                    required
                    placeholder="Price you paid to buy"
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>Price you paid to purchase this item</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Sell Price / Unit Price (PKR) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.unitPrice}
                    onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) || 0 })}
                    min="0"
                    required
                    placeholder="Price you sell to customer"
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>Price you sell this item to customers</small>
                </div>
                <div className="form-group">
                  <label>Expected Profit per Unit</label>
                  <input
                    type="text"
                    value={formatPrice((formData.unitPrice || 0) - (formData.purchasePrice || 0))}
                    readOnly
                    style={{ 
                      backgroundColor: '#f5f5f5', 
                      color: (formData.unitPrice - formData.purchasePrice) >= 0 ? '#137333' : '#c5221f',
                      fontWeight: 'bold'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>Calculated automatically (Sell Price - Purchase Price)</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Min Stock Level</label>
                  <input
                    type="number"
                    value={formData.minStockLevel}
                    onChange={(e) => setFormData({ ...formData, minStockLevel: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Max Stock Level</label>
                  <input
                    type="number"
                    value={formData.maxStockLevel}
                    onChange={(e) => setFormData({ ...formData, maxStockLevel: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Batch Number</label>
                  <input
                    type="text"
                    value={formData.batchNumber}
                    onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                    placeholder="Batch number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Storage location"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {selectedItem ? 'Update Stock' : 'Add Stock'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddCustomProduct && (
        <AddCustomProduct
          initialName={productSearch}
          onClose={() => setShowAddCustomProduct(false)}
          onSuccess={handleCustomProductAdded}
          token={authToken}
        />
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Stock Batch</h2>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Product:</strong> {itemToDelete?.medicine_name || itemToDelete?.custom_product_name}</p>
              {itemToDelete?.batch_number && <p><strong>Batch Number:</strong> {itemToDelete.batch_number}</p>}
              <p><strong>Quantity:</strong> {itemToDelete?.quantity || 0}</p>
              <p style={{ color: '#dc3545', marginTop: '15px' }}>
                Are you sure you want to delete this stock batch? This action cannot be undone, but the product will remain in the database.
              </p>
              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>Reason for Deletion *</label>
                <textarea
                  value={deleteComment}
                  onChange={(e) => setDeleteComment(e.target.value)}
                  placeholder="Please provide a reason for deleting this stock batch..."
                  required
                  rows="4"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button type="button" className="btn-cancel" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-delete" onClick={handleDeleteConfirm} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}>
                  Delete Stock Batch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockManagement;

